import { Button } from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/molecules/Modal/Modal";
import {
  FEATURE_FLAG_ALL_VALUE,
  FeatureFlagRuleFormData,
  featureFlagRuleSchema,
} from "../../schemas/featureFlag.schema";
import {
  FEATURE_FLAG_COUNTRIES,
  FEATURE_FLAG_NAMES,
  FeatureFlagDto,
  FeatureFlagInput,
} from "../../types/featureFlag.definition";
import { envConfig } from "../../utils/config-utils";

interface FeatureFlagRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: FeatureFlagInput) => void;
  /**
   * The rule being edited, or null/undefined when creating a new one.
   * When set, flagName is locked to the existing rule's value — only
   * country/enabled can be changed for an existing rule. environment is
   * never user-editable (see handleFormSubmit).
   */
  initial?: FeatureFlagDto | null;
  isSaving: boolean;
  /**
   * Inline error to surface above the form actions (e.g. duplicate rule).
   * The parent owns the message and clears it when desired.
   */
  inlineError?: string | null;
}

export const FeatureFlagRuleModal: React.FC<FeatureFlagRuleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initial,
  isSaving,
  inlineError,
}) => {
  const { t } = useTranslation();
  const prevIsOpen = useRef(false);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<FeatureFlagRuleFormData>({
    resolver: zodResolver(featureFlagRuleSchema(t)),
    mode: "onChange",
    defaultValues: {
      flagName: FEATURE_FLAG_NAMES[0],
      country: FEATURE_FLAG_ALL_VALUE,
      enabled: true,
    },
  });

  // Reset form only when the modal opens (false -> true). Avoid resetting on
  // every render so the user's input survives re-renders.
  useEffect(() => {
    if (!isOpen || prevIsOpen.current === isOpen) {
      if (!isOpen) prevIsOpen.current = false;
      return;
    }
    prevIsOpen.current = isOpen;

    reset({
      flagName: initial?.flagName ?? FEATURE_FLAG_NAMES[0],
      country: initial?.country ?? FEATURE_FLAG_ALL_VALUE,
      enabled: initial?.enabled ?? true,
    });
  }, [isOpen, initial, reset]);

  const handleFormSubmit = (data: FeatureFlagRuleFormData) => {
    const input: FeatureFlagInput = {
      // Locked to the existing rule's flagName when editing — changing a
      // rule's flagName would effectively re-target a different flag, so
      // that field is read-only once a rule exists (see FEATURE_FLAG_NAMES
      // Select's `disabled` prop below).
      flagName: initial ? initial.flagName : data.flagName,
      // Not user-editable: this admin dashboard is already deployed
      // per-environment, so a new rule is scoped to that environment.
      // Editing an existing rule leaves its environment untouched
      // (including a pre-existing global/null-environment rule).
      environment: initial ? initial.environment : envConfig.env,
      country:
        data.country === FEATURE_FLAG_ALL_VALUE
          ? null
          : (data.country as FeatureFlagInput["country"]),
      enabled: data.enabled,
    };
    onSubmit(input);
  };

  const flagOptions = FEATURE_FLAG_NAMES.map(name => ({
    value: name,
    label: t(`admin.governance.featureFlag.names.${name}`),
  }));

  const countryOptions = [
    { value: FEATURE_FLAG_ALL_VALUE, label: t("admin.governance.scope.all") },
    ...FEATURE_FLAG_COUNTRIES.map(c => ({
      value: c,
      label: t(`admin.governance.countries.${c}`),
    })),
  ];

  const enabled = watch("enabled");

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={
        initial
          ? t("admin.governance.featureFlag.editTitle")
          : t("admin.governance.featureFlag.createTitle")
      }
      size="md"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="p-6 space-y-4">
          {inlineError && (
            <div className="rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
              {inlineError}
            </div>
          )}

          <Controller
            name="flagName"
            control={control}
            render={({ field }) => (
              <Select
                label={t("admin.governance.featureFlag.form.flagName")}
                options={flagOptions}
                value={field.value}
                onChange={value => field.onChange(value as string)}
                error={errors.flagName?.message}
                disabled={!!initial}
              />
            )}
          />

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Select
                label={t("admin.governance.form.country")}
                options={countryOptions}
                value={field.value}
                onChange={value => field.onChange(value as string)}
                error={errors.country?.message}
              />
            )}
          />

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={e => setValue("enabled", e.target.checked)}
                className="w-4 h-4 rounded border-neutral-300 text-ews-primary focus:ring-ews-primary"
              />
              <span className="text-sm font-medium text-neutral-700">
                {t("admin.governance.form.enabled")}
              </span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end space-x-3">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={isSaving || !isValid}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
